import { type DocumentDetail, type FileDownloadUrl } from '@apm/contracts';
import { apiFetch } from '../../lib/http';
import { uploadFile } from '../../lib/upload';
import { type DocumentStorage, type SavedDocument } from '../my-files/editor/document-storage';
import {
  documentReadMaxBytes,
  fetchFileBytes,
  fetchFileText,
} from '../my-files/preview/file-content';
import { type PreviewSource } from '../my-files/preview/preview-source';
import { documentApi } from './api';
import { documentTitleOf } from './upload-documents';

/**
 * Hộp soạn lưu thành **tài liệu của dự án** — APM-PDOC-b.
 *
 * - Lưu lần đầu: tệp lên vùng dự án (presign → PUT thẳng S3 → commit, luật #3),
 *   rồi `POST /projects/:id/documents` trỏ tới nó — cùng hai bước với form "Tài
 *   liệu mới" của `DocumentsPanel`. Tiêu đề là tên người dùng gõ.
 * - Các lần sau: **Lưu ghi đè bản mới nhất** (`POST /documents/:id/save`,
 *   người dùng chốt 2026-10-01 — thay cho "mỗi lần Lưu một phiên bản" của
 *   2026-09-28, vì lưu hai lần trong một phút thành hai tệp cùng tên). Server
 *   tự thêm bản khi bản mới nhất đã gửi duyệt / đã duyệt / của người khác, hoặc
 *   khi có bản mới hơn chỗ hộp soạn đang đứng (`baseVersionNo`). Muốn giữ mốc
 *   thì "Lưu thành phiên bản mới" (`saveVersion`). Đổi tên trong hộp soạn thì
 *   đổi luôn tiêu đề.
 * - Ảnh chèn vào là **tệp dự án**: thành viên nào đọc được tài liệu thì mở được
 *   ảnh. Ảnh cá nhân thì chỉ người soạn thấy.
 * - Mở để sửa (APM-PDOC-c): đọc tệp của **phiên bản mới nhất** — cùng bản mà
 *   lần Lưu kế tiếp nối lên sau nó. Bản chỉ có chữ gõ tay thì đọc chữ ấy.
 *
 * `onSaved` nhận tài liệu vừa ghi — nơi gọi làm mới danh sách của mình.
 */
export function projectDocumentStorage(
  projectId: string,
  onSaved?: (document: DocumentDetail) => void,
  options: {
    /** Ghi chú thay đổi cho phiên bản sắp lưu — ô "Có gì thay đổi" (APM-PDOC-e). */
    readonly changeNote?: () => string | undefined;
    /**
     * Lần Lưu đầu của tài liệu có sẵn là **một phiên bản mới** — "Tạo phiên bản
     * mới → Mở để sửa" (APM-PDOC-e). Các lần sau ghi đè chính bản vừa tạo.
     */
    readonly firstSaveAsVersion?: boolean;
  } = {},
): DocumentStorage {
  /*
    Bản hộp soạn đang đứng, theo tài liệu: bản đã mở (`read`) hoặc vừa lưu.
    Server so nó với bản mới nhất — ai vừa thêm bản sau thì lần lưu thành bản
    mới thay vì đè lên bản của họ.
  */
  const base = new Map<string, number>();
  const note = (explicit?: string): string | undefined =>
    explicit?.trim() || options.changeNote?.() || undefined;

  /** Đổi tiêu đề nếu tên tệp đổi, rồi tải tệp vào vùng của tài liệu. */
  const prepare = async (file: File, previous: SavedDocument): Promise<string> => {
    const title = documentTitleOf(file.name);
    if (title !== documentTitleOf(previous.name)) {
      await documentApi.update(previous.id, { title });
    }
    return uploadFile(file, { projectId, documentId: previous.id });
  };

  const finish = (document: DocumentDetail, file: File, newVersion: boolean): SavedDocument => {
    base.set(document.id, document.latestVersionNo);
    onSaved?.(document);
    return {
      id: document.id,
      name: file.name,
      ...(newVersion ? { newVersion: { versionNo: document.latestVersionNo } } : {}),
    };
  };

  const addVersion = async (
    file: File,
    previous: SavedDocument,
    changeNote: string | undefined,
  ): Promise<SavedDocument> => {
    const fileId = await prepare(file, previous);
    const document = await documentApi.addVersion(previous.id, {
      fileId,
      ...(changeNote ? { changeNote } : {}),
    });
    return finish(document, file, true);
  };

  return {
    // Checklist "Tạo task" tạo việc ở chính dự án này (APM-CD2-e).
    projectId,
    // Song ngữ (APM-BIL-b): bản dịch theo id tài liệu, sống qua mọi phiên bản.
    translationRef: (saved) => ({ docKind: 'document', docKey: saved.id }),
    save: async (file, previous) => {
      if (!previous) {
        const fileId = await uploadFile(file, { projectId, purpose: 'document' });
        const document = await documentApi.create(projectId, {
          title: documentTitleOf(file.name),
          fileId,
        });
        // Bản 1 vừa tạo: với người dùng đó là "lưu", không phải "một phiên bản mới".
        return finish(document, file, false);
      }
      const baseVersionNo = base.get(previous.id);
      if (baseVersionNo === undefined && options.firstSaveAsVersion) {
        return addVersion(file, previous, note());
      }
      const fileId = await prepare(file, previous);
      const changeNote = note();
      const result = await documentApi.save(previous.id, {
        fileId,
        baseVersionNo: baseVersionNo ?? (await documentApi.detail(previous.id)).latestVersionNo,
        ...(changeNote ? { changeNote } : {}),
      });
      return finish(result.document, file, result.outcome === 'new_version');
    },
    saveVersion: (file, previous, explicit) => addVersion(file, previous, note(explicit)),
    uploadImage: async (file, saved) => {
      const fileId = await uploadFile(file, {
        projectId,
        ...(saved ? { documentId: saved.id } : { purpose: 'document' as const }),
      });
      return { fileId, url: await signedUrl(fileId) };
    },
    read: async (document) => {
      const detail = await documentApi.detail(document.id);
      // `versions` xếp mới nhất trước (DocumentRepository.versions).
      const latest = detail.versions[0];
      // Lần Lưu kế tiếp nối lên **đúng** bản này (APM-PDOC-f) — trừ khi hộp
      // soạn mở để tạo bản mới (`firstSaveAsVersion`).
      if (!options.firstSaveAsVersion) base.set(document.id, detail.latestVersionNo);
      // Bản chỉ có chữ gõ tay — mở như markdown (`editableFormatOf`).
      if (!latest?.fileId) return detail.latestContent ?? '';
      return fetchFileText(await signedUrl(latest.fileId), documentReadMaxBytes(document.name));
    },
    // Bảng tính (APM-XLE-d): byte thô của bản mới nhất, cùng mốc `base` như `read`.
    readBytes: async (document, maxBytes) => {
      const detail = await documentApi.detail(document.id);
      const latest = detail.versions[0];
      if (!options.firstSaveAsVersion) base.set(document.id, detail.latestVersionNo);
      if (!latest?.fileId) throw new Error('Tài liệu này không có tệp bảng tính.');
      return fetchFileBytes(await signedUrl(latest.fileId), maxBytes);
    },
    urlsOf: PROJECT_FILES_SOURCE.urlsOf,
  };
}

async function signedUrl(fileId: string): Promise<string> {
  return (await apiFetch<FileDownloadUrl>(`/files/${fileId}/url`)).url;
}

/**
 * URL đã ký cho **tệp dự án** — khung xem trước (`PreviewSourceProvider`,
 * APM-PDOC-d) và hộp soạn dùng chung. `GET /files/:id/url` kiểm `document:read`
 * trong dự án của tệp.
 */
export const PROJECT_FILES_SOURCE: PreviewSource = {
  key: 'project-files',
  downloadUrl: async (fileId) => ({ url: await signedUrl(fileId) }),
  /*
    Tệp dự án không có lối lấy URL theo lô như `/me/files/urls`, nên mỗi ảnh một
    lời gọi — song song, vì một tài liệu thường chỉ có vài ảnh. Ảnh không mở
    được (đã xoá, không còn quyền) thành `null`: trình xem vẽ khung "ảnh không
    tải được" thay vì vỡ cả tài liệu.
  */
  urlsOf: async (fileIds) =>
    Object.fromEntries(
      await Promise.all(
        fileIds.map(async (id) => [id, await signedUrl(id).catch(() => null)] as const),
      ),
    ),
};
